import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, BookImage, ImagePlus, MoreHorizontal, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ActionMenu } from '@/components/ActionMenu'
import { AlbumCover } from '@/components/AlbumCover'
import { AlbumPeek } from '@/components/AlbumPeek'
import { UploadTray } from '@/components/AddPhotos'
import { CreateAction } from '@/components/CreateAction'
import { NewAlbum } from '@/components/NewAlbum'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { ModernPhotos } from '@/components/modern/ModernPhotos'
import { PhotoGrid } from '@/components/PhotoGrid'
import { PickOne } from '@/components/PickOne'
import { FilterButton, type Filter } from '@/components/Filters'
import { Pills } from '@/components/Pills'
import { PickedBar, SelectToggle } from '@/components/Selection'
import { SendPhotos } from '@/components/SendTo'
import { ShareWith } from '@/components/ShareWith'
import { Avatar } from '@/components/tree/Avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAddPhotos, type Adding } from '@/lib/addPhotos'
import { api } from '@/lib/api'
import {
  albumPhotoUrl,
  span,
  streamGroups,
  useAlbums,
  useFoundPhotos,
  useStream,
  type AlbumSummary,
  type Albums,
  type Browse,
  type Collection,
  type LoosePhotos,
  type StreamBy,
  type StreamGroup,
} from '@/lib/albums'
import type { Family, Space } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { handOffPhotos, takePhotosFor } from '@/lib/handoff'
import { PHOTO_TYPES } from '@/lib/photos'
import { remember, remembered } from '@/lib/remember'
import { useSelection, type Selection } from '@/lib/selection'
import { ONLY_ME, useCurrentSpace, useLastShare, type Audience } from '@/lib/spaces'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PlaceHeader, WithFamily } from './FamilyPage'
import { JustSeeing } from '@/components/JustSeeing'
import { usePosts } from '@/lib/sections'
import { Loader } from '@/components/Loader'
import { BarRow } from '@/components/nav/BarRow'

export const PhotosPage = () => <WithFamily>{(family) => <Photos family={family} />}</WithFamily>

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

type View = 'albums' | 'stream'
const VIEW_KEY = 'gatheredfrom:photos-view'
// A new name when ungrouped became the usual, so a grouping remembered from before doesn't hide it.
const GROUP_KEY = 'gatheredfrom:photos-grouping'

// The family's photos, two ways. Albums: event albums ("Thanksgiving 2024"), shelved in collections ("Holidays"),
// with ways in by person, year and tag, and the photos in no album. The stream: every photo at once, newest added
// first like one big collage, or grouped by year, by home or by who's in it, to page through. Either can show only what you added yourself, to look over and tidy.
// Photos can be added without an album, and picked later to send to one. In the Modern style the owner can pick
// (components/modern/), the same photos are laid out as rings, tags and a wall.
function Photos({ family }: { family: Family }) {
  usePageFrame(family.id, { title: 'Photos' })
  const me = useMe().data
  const modern = me?.photosStyle === 'modern'
  const space = useCurrentSpace(family)
  const albums = useAlbums(family.id, space?.id)
  const [starting, setStarting] = useState(false)
  // Photos picked from the "+" before saying which album they join, and then (for no album) who sees them.
  const [picked, setPicked] = useState<File[] | null>(null)
  const [noAlbum, setNoAlbum] = useState(false)
  // Photos picked from Home's "+" arrive here to be asked the same.
  useEffect(() => {
    const files = takePhotosFor(family.id)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- taken once from memory, as picking them here would
    if (files.length) setPicked(files)
  }, [family.id])
  // Photos added to no album upload here, so they carry on while you look around Photos.
  const addingLoose = useAddPhotos({ familyId: family.id, spaceIds: [] }, family.id, () => {})
  // Picking photos to send to an album, in whichever view is showing.
  const selection = useSelection()
  const picker = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tag = params.get('tag')
  const person = params.get('person')
  const year = params.get('year')
  const loose = params.get('loose') === '1'
  const by: Browse | null = tag ? { tag } : person ? { person } : year ? { year } : null
  const first = albums.data?.albums.length === 0 && !albums.data.loose?.photos
  const groupings: StreamBy[] =
    family.kind === 'family' ? ['added', 'year', 'family', 'person'] : ['added', 'year', 'person']
  const view = (params.get('view') as View | null) ?? remembered<View>(VIEW_KEY, ['albums', 'stream'], 'albums')
  const fromUrl = params.get('group') as StreamBy | null
  const group = fromUrl && groupings.includes(fromUrl) ? fromUrl : remembered<StreamBy>(GROUP_KEY, groupings, 'added')
  const mine = params.get('mine') === '1'
  const choose = (change: { view?: View; group?: StreamBy; mine?: boolean }) => {
    if (change.view) remember(VIEW_KEY, change.view)
    if (change.group) remember(GROUP_KEY, change.group)
    const next = { view: change.view ?? view, group: change.group ?? group, mine: change.mine ?? mine }
    const query = new URLSearchParams({ view: next.view })
    if (next.view === 'stream') query.set('group', next.group)
    if (next.mine) query.set('mine', '1')
    selection.stop()
    setParams(query, { replace: true })
  }

  return (
    <div className="flex flex-col gap-10 pt-6 pb-8 nav:gap-12 nav:pt-14">
      <input
        ref={picker}
        type="file"
        accept={PHOTO_TYPES}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="choose-photos"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (!files.length) return
          setPicked(files)
          // On the page of photos in no album, they go there: just who sees them is asked.
          if (loose) setNoAlbum(true)
        }}
      />
      {/* On a phone the heading is in the top bar, so this row takes no room there. */}
      <div className={cn(page, 'flex flex-wrap items-end justify-between gap-4 max-nav:sr-only')}>
        <PlaceHeader family={family} title="Photos" />
      </div>
      {albums.data && (!by || modern) && !selection.selecting && (
        <CreateAction
          section="photos"
          title="Add to photos"
          actions={[
            { label: 'Add photos', icon: ImagePlus, onSelect: () => picker.current?.click() },
            ...(loose ? [] : [{ label: 'New album', icon: BookImage, onSelect: () => setStarting(true) }]),
          ]}
        />
      )}
      <FormError error={albums.error} />
      {albums.data &&
        (loose ? (
          <Loose family={family} space={space} mine={mine} adding={addingLoose} selection={selection} />
        ) : modern && !first ? (
          <ModernPhotos
            family={family}
            space={space}
            albums={albums.data}
            mine={mine}
            onMine={(next) => {
              const query = new URLSearchParams(params)
              if (next) query.set('mine', '1')
              else query.delete('mine')
              selection.stop()
              setParams(query, { replace: true })
            }}
            selection={selection}
            moments={me?.sections.moments ?? false}
            shelves={
              <Shelves
                family={family}
                albums={mine ? onlyMine(albums.data) : albums.data}
                onStart={() => setStarting(true)}
                mine={mine}
                tight
              />
            }
            sendPicked={(photos) => (
              <SendPicked
                family={family}
                space={space}
                selection={selection}
                photos={photos.map((p) => ({ id: p.id, album: p.album ?? null, canEdit: p.canEdit ?? false }))}
              />
            )}
          />
        ) : by ? (
          <Found family={family} space={space} by={by} albums={albums.data} selection={selection} />
        ) : first ? (
          <FirstAlbum onStart={() => setStarting(true)} onAdd={() => picker.current?.click()} />
        ) : (
          <>
            <ViewBar view={view} group={group} groupings={groupings} mine={mine} onChange={choose} />
            {view === 'stream' ? (
              <Stream
                key={`${group}-${mine}`}
                family={family}
                space={space}
                by={group}
                mine={mine}
                selection={selection}
              />
            ) : mine ? (
              <Shelves family={family} albums={onlyMine(albums.data)} onStart={() => setStarting(true)} mine />
            ) : (
              <>
                <Faces family={family} albums={albums.data} />
                <Shelves family={family} albums={albums.data} onStart={() => setStarting(true)} />
                <Browsing family={family} albums={albums.data} />
              </>
            )}
          </>
        ))}
      {albums.data && (
        <NewAlbum
          key={`${space?.id}-${starting}`}
          open={starting}
          onOpenChange={setStarting}
          family={family}
          space={space}
          collections={albums.data.collections}
          first={first}
          photos={picked ?? undefined}
        />
      )}
      {albums.data && (
        <PickOne
          open={!!picked && !starting && !noAlbum}
          onOpenChange={(open) => !open && setPicked(null)}
          title={picked ? `Add ${plural(picked.length, 'photo')} to which album?` : 'Which album?'}
          description="An album is up to you. You can send them to one any time."
          icon={ImagePlus}
          choices={[
            { id: '', title: 'No album', detail: 'Add them to Photos on their own', cover: null },
            ...albums.data.albums
              .filter((x) => !x.elsewhere)
              .map((x) => ({
                id: x.id,
                title: x.title,
                detail: plural(x.photos, 'photo'),
                cover: x.covers[0] ? albumPhotoUrl(x.covers[0], 'thumb') : null,
              })),
          ]}
          onPick={(id) => {
            if (!id) return setNoAlbum(true)
            handOffPhotos(id, picked ?? [])
            navigate(`/album/${id}`)
          }}
          fresh={{ label: 'A new album', onSelect: () => setStarting(true) }}
        />
      )}
      {picked && (
        <WhoSees
          open={noAlbum}
          onOpenChange={(open) => {
            setNoAlbum(open)
            if (!open) setPicked(null)
          }}
          family={family}
          n={picked.length}
          onChoose={(who) => {
            addingLoose.add(picked, { familyId: family.id, ...who })
            setNoAlbum(false)
            setPicked(null)
            const query = new URLSearchParams({ loose: '1' })
            if (mine) query.set('mine', '1')
            navigate(`/circle/${family.id}/photos?${query}`)
          }}
        />
      )}
    </div>
  )
}

// Just the albums you started, and the collections they sit on.
function onlyMine(albums: Albums): Albums {
  const shelf = albums.albums.filter((x) => x.mine)
  const shelved = new Set(shelf.map((x) => x.collectionId))
  const loose = albums.loose && { ...albums.loose, photos: albums.loose.mine, covers: albums.loose.mineCovers }
  return { ...albums, albums: shelf, collections: albums.collections.filter((x) => shelved.has(x.id)), loose }
}

const GROUP_LABEL: Record<StreamBy, string> = { added: 'None', year: 'Year', family: 'Family', person: 'Person' }

// How to look at the photos: as albums or every photo in one stream, and behind one button how the stream is grouped
// and whether it's everyone's or only your own.
function ViewBar({
  view,
  group,
  groupings,
  mine,
  onChange,
}: {
  view: View
  group: StreamBy
  groupings: StreamBy[]
  mine: boolean
  onChange: (change: { view?: View; group?: StreamBy; mine?: boolean }) => void
}) {
  const filters: Filter[] = [
    ...(view === 'stream'
      ? [
          {
            heading: 'Group by',
            label: 'Group the stream by',
            value: group,
            options: groupings.map((x): [string, string] => [x, GROUP_LABEL[x]]),
            onChange: (g: string) => onChange({ group: g as StreamBy }),
          },
        ]
      : []),
    {
      heading: 'Whose',
      label: 'Whose',
      value: mine ? 'mine' : 'everyone',
      usual: 'everyone',
      options: [
        ['everyone', 'Everyone’s'],
        ['mine', 'Only mine'],
      ],
      onChange: (w) => onChange({ mine: w === 'mine' }),
    },
  ]
  return (
    <BarRow
      className={cn(page, 'flex items-center justify-between gap-4')}
      barClassName="flex min-w-0 flex-1 items-center justify-between gap-2"
    >
      <Pills
        label="Show photos as"
        value={view}
        options={[
          ['albums', 'Albums'],
          ['stream', 'Photos'],
        ]}
        onChange={(v) => onChange({ view: v })}
      />
      <FilterButton title="Filter" filters={filters} />
    </BarRow>
  )
}

// Every photo, a page at a time, in groups. More load as you near the end, and a button does the same for anyone who
// would rather ask.
function Stream({
  family,
  space,
  by,
  mine,
  selection,
}: {
  family: Family
  space: Space | null
  by: StreamBy
  mine: boolean
  selection: Selection
}) {
  const stream = useStream(family.id, by, mine, space?.id)
  const groups = useMemo(() => streamGroups(stream.data?.pages ?? []), [stream.data])
  const all = useMemo(() => groups.flatMap((x) => x.photos), [groups])
  const end = useRef<HTMLDivElement>(null)
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = stream
  useEffect(() => {
    const target = end.current
    if (!target || !hasNextPage) return
    const watch = new IntersectionObserver(
      (seen) => {
        if (seen.some((x) => x.isIntersecting) && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '1200px 0px' },
    )
    watch.observe(target)
    return () => watch.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn(page, 'flex flex-col gap-12')}>
      <FormError error={stream.error} />
      {stream.data && groups.length === 0 && (
        <p className="text-muted-foreground">
          {mine ? 'You haven’t added any photos yet.' : 'No photos yet. Add the first from the “+”.'}
        </p>
      )}
      {groups.map(({ group, photos }, i) => (
        <section key={group.key} className="flex flex-col gap-4" aria-labelledby={`g-${by}-${group.key}`}>
          <div className="flex items-start justify-between gap-4">
            <StreamHeading family={family} by={by} group={group} />
            {i === 0 && <SelectToggle selection={selection} className="-mt-1 shrink-0" />}
          </div>
          <PhotoGrid
            photos={photos}
            layout="rows"
            selected={selection.selected}
            onToggle={selection.selecting ? selection.toggle : undefined}
          />
        </section>
      ))}
      <SendPicked family={family} space={space} selection={selection} photos={all} />
      <div ref={end} className="flex justify-center">
        {hasNextPage &&
          (isFetchingNextPage ? (
            <Loader label="Loading more photos" now />
          ) : (
            <Button variant="outline" onClick={() => void fetchNextPage()}>
              Show more photos
            </Button>
          ))}
      </div>
    </div>
  )
}

function StreamHeading({ family, by, group }: { family: Family; by: StreamBy; group: StreamGroup }) {
  const id = `g-${by}-${group.key}`
  const count = <span className="text-muted-foreground">{plural(group.photos, 'photo')}</span>
  // Not grouped: no title over the collage, just how many and in what order.
  if (by === 'added')
    return (
      <div>
        <h2 id={id} className="sr-only">
          {group.label}
        </h2>
        <p className="text-muted-foreground">{plural(group.photos, 'photo')}, newest added first</p>
      </div>
    )
  if (by === 'person' && group.key) {
    const who = { id: group.key, name: group.label, portrait: group.portrait ?? null }
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Avatar person={who} className="size-11 text-lg shadow-none" />
        <h2 id={id} className="text-title">
          {group.label}
        </h2>
        {count}
        <Link
          to={`/person/${group.key}`}
          className={cn('text-sm text-brand underline underline-offset-4', link)}
          aria-label={`Open ${group.label}’s page`}
        >
          Open their page
        </Link>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {by === 'family' && group.key && family.kind === 'family' && <p className="eyebrow">Shared from</p>}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id={id} className="text-title">
          {group.label}
        </h2>
        {count}
      </div>
    </div>
  )
}

function FirstAlbum({ onStart, onAdd }: { onStart: () => void; onAdd: () => void }) {
  const posting = usePosts('photos')
  return (
    <div className={page}>
      <div className="flex flex-col items-center gap-6 rounded-[2.5rem] table-surface px-6 py-12 text-center sm:py-16">
        <AlbumCover covers={[]} index={1} className="w-36 sm:w-44" />
        <div className="flex max-w-md flex-col gap-2">
          <p className="text-title font-semibold tracking-tight text-balance">Open the first album.</p>
          <p className="text-table-muted">
            One album for each gathering or season: Thanksgiving 2024, summers at the lake. Add the photos first and
            sort them after.
          </p>
        </div>
        {posting ? (
          <div className="flex flex-col items-center gap-2">
            <Button size="lg" onClick={onStart}>
              <Plus aria-hidden /> Start the first album
            </Button>
            <Button variant="ghost" onClick={onAdd}>
              <ImagePlus aria-hidden /> Or add photos without one
            </Button>
          </div>
        ) : (
          <JustSeeing />
        )}
      </div>
    </div>
  )
}

const ways = (family: Family, key: string, value: string) =>
  `/circle/${family.id}/photos?${new URLSearchParams({ [key]: value })}`

// The people in the photos, as a row of faces to tap, like the row along the top of a phone's photo app.
function Faces({ family, albums }: { family: Family; albums: Albums }) {
  if (albums.people.length === 0) return null
  return (
    <section className="flex flex-col gap-3" aria-labelledby="photo-people">
      <h2 id="photo-people" className={cn(page, 'eyebrow')}>
        People
      </h2>
      <ul className="scroll-row gap-4 px-5 sm:gap-5 sm:px-8 lg:px-[max(3rem,calc((100%_-_var(--gallery))/2_+_3rem))]">
        {albums.people.map((p) => (
          <li key={p.id} className="shrink-0 snap-start">
            <Link
              to={ways(family, 'person', p.id)}
              className={cn('group flex w-20 flex-col items-center gap-2 rounded-2xl p-1 text-center sm:w-24', link)}
            >
              <span className="rounded-full p-[3px] ring-2 ring-border transition-[box-shadow] group-hover:ring-input">
                <Avatar person={p} className="size-16 text-2xl shadow-none sm:size-20 sm:text-3xl" />
              </span>
              <span className="flex w-full flex-col">
                <span className="truncate text-sm font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">{plural(p.photos, 'photo')}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

// The other ways in: the year the photos are from (the date set, or else the year added), and what they're tagged.
function Browsing({ family, albums }: { family: Family; albums: Albums }) {
  const rows = [
    {
      title: 'Years',
      items: albums.years.map((x) => ({
        key: x.year,
        label: x.year,
        to: ways(family, 'year', x.year),
        photos: x.photos,
      })),
    },
    {
      title: 'Tags',
      items: albums.tags.map((x) => ({ key: x.tag, label: x.tag, to: ways(family, 'tag', x.tag), photos: x.photos })),
    },
  ].filter((x) => x.items.length > 0)
  if (rows.length === 0) return null
  return (
    <section className={cn(page, 'flex flex-col gap-6 border-t pt-8')} aria-labelledby="find-photos">
      <h2 id="find-photos" className="text-title">
        Find photos
      </h2>
      {rows.map((row) => (
        <div key={row.title} className="flex flex-col gap-2.5">
          <h3 className="eyebrow">{row.title}</h3>
          <ul className="flex flex-wrap gap-2">
            {row.items.map((x) => (
              <li key={x.key}>
                <Link
                  to={x.to}
                  className={cn(
                    'flex min-h-11 items-center gap-2 rounded-full bg-secondary px-4 text-sm transition-colors hover:bg-accent',
                    link,
                  )}
                >
                  {x.label}
                  <span className="text-muted-foreground">{x.photos}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

function Found({
  family,
  space,
  by,
  albums,
  selection,
}: {
  family: Family
  space: Space | null
  by: Browse
  albums: Albums
  selection: Selection
}) {
  const found = useFoundPhotos(family.id, by, space?.id)
  const person = 'person' in by ? albums.people.find((x) => x.id === by.person) : undefined
  const title =
    'tag' in by ? `Tagged “${by.tag}”` : 'year' in by ? `From ${by.year}` : `Photos of ${person?.name ?? 'someone'}`
  const photos = found.data?.photos ?? []
  return (
    <div className={cn(page, 'flex flex-col gap-6')}>
      <div className="flex flex-col gap-3">
        <Link
          to={`/circle/${family.id}/photos`}
          className={cn(
            '-ml-2 flex items-center gap-1.5 self-start rounded-full py-1 pr-3 pl-2 text-sm hover:bg-secondary',
            link,
          )}
        >
          <ArrowLeft className="size-4" aria-hidden /> All albums
        </Link>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className="text-title">{title}</h2>
          {found.data && <span className="text-muted-foreground">{plural(photos.length, 'photo')}</span>}
          {person && (
            <Link to={`/person/${person.id}`} className={cn('text-sm text-brand underline underline-offset-4', link)}>
              Open their page
            </Link>
          )}
          <span className="flex-1" />
          {photos.length > 0 && <SelectToggle selection={selection} />}
        </div>
      </div>
      <FormError error={found.error} />
      {found.data &&
        (photos.length === 0 ? (
          <p className="text-muted-foreground">No photos here yet.</p>
        ) : (
          <section aria-label={title}>
            <PhotoGrid
              photos={photos}
              selected={selection.selected}
              onToggle={selection.selecting ? selection.toggle : undefined}
            />
          </section>
        ))}
      <SendPicked family={family} space={space} selection={selection} photos={photos} />
    </div>
  )
}

// The photos added on their own, in no album, newest first: where photos added without an album land, to look over
// and send to albums when there's time.
function Loose({
  family,
  space,
  mine,
  adding,
  selection,
}: {
  family: Family
  space: Space | null
  mine: boolean
  adding: Adding
  selection: Selection
}) {
  const found = useFoundPhotos(family.id, { loose: '1' }, space?.id, mine)
  const photos = found.data?.photos ?? []
  return (
    <div className={cn(page, 'flex flex-col gap-6')}>
      <div className="flex flex-col gap-3">
        <Link
          to={`/circle/${family.id}/photos${mine ? '?mine=1' : ''}`}
          className={cn(
            '-ml-2 flex items-center gap-1.5 self-start rounded-full py-1 pr-3 pl-2 text-sm hover:bg-secondary',
            link,
          )}
        >
          <ArrowLeft className="size-4" aria-hidden /> All albums
        </Link>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className="text-title">Not in an album</h2>
          {found.data && <span className="text-muted-foreground">{plural(photos.length, 'photo')}</span>}
          <span className="flex-1" />
          {photos.length > 0 && <SelectToggle selection={selection} />}
        </div>
        <p className="text-sub text-muted-foreground">
          Photos added on their own. Pick some and send them to an album whenever you like.
        </p>
      </div>
      <UploadTray adding={adding} />
      <FormError error={found.error} />
      {found.data &&
        (photos.length === 0 ? (
          adding.pending === 0 && (
            <p className="text-muted-foreground">
              {mine ? 'You have no photos outside an album.' : 'Every photo is in an album.'}
            </p>
          )
        ) : (
          <section aria-label="Photos not in an album">
            <PhotoGrid
              photos={photos}
              layout="rows"
              selected={selection.selected}
              onToggle={selection.selecting ? selection.toggle : undefined}
            />
          </section>
        ))}
      <SendPicked family={family} space={space} selection={selection} photos={photos} />
    </div>
  )
}

// While picking: the bar that says how many are picked and sends them to an album, which only someone who may change
// every one of them can do (whoever added each, or keeps its album).
function SendPicked({
  family,
  space,
  selection,
  photos,
}: {
  family: Family
  space: Space | null
  selection: Selection
  photos: { id: string; album: { id: string } | null; canEdit: boolean }[]
}) {
  const [sending, setSending] = useState(false)
  const [starting, setStarting] = useState(false)
  const albums = useAlbums(family.id)
  if (!selection.selecting) return null
  const picked = photos.filter((x) => selection.selected.has(x.id))
  const mayChange = picked.every((x) => x.canEdit)
  return (
    <>
      <PickedBar
        label="Send the photos you picked"
        count={picked.length}
        noun="photo"
        actions={[{ label: 'Send to album', onSelect: () => setSending(true), disabled: !mayChange }]}
        onPickAll={() => selection.pickAll(photos.map((x) => x.id))}
        onDone={selection.stop}
      />
      {picked.length > 0 && !mayChange && (
        <p role="status" className="sr-only">
          Only photos you added, or whose album you keep, can be sent to an album.
        </p>
      )}
      <SendPhotos
        open={sending && !starting}
        onOpenChange={setSending}
        familyId={family.id}
        photos={picked.map((x) => x.id)}
        inAlbum={picked.some((x) => x.album)}
        onSent={() => {
          setSending(false)
          selection.stop()
        }}
        onNewAlbum={() => setStarting(true)}
      />
      {starting && (
        <NewAlbum
          open
          onOpenChange={(open) => !open && setStarting(false)}
          family={family}
          space={space}
          collections={albums.data?.collections ?? []}
          first={false}
          send={picked.map((x) => x.id)}
        />
      )}
    </>
  )
}

// "Who sees them?" for photos added to no album: kept just for you to start with, as everything you add on your own
// is, or shared now, in this circle or your others. Sending them to an album later adds that album's people.
function WhoSees({
  open,
  onOpenChange,
  family,
  n,
  onChoose,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  family: Family
  n: number
  onChoose: (who: Audience) => void
}) {
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() => lastShare.start(ONLY_ME))
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault()
            lastShare.remember(who)
            onChoose(who)
          }}
        >
          <DialogHeader>
            <DialogTitle>Who sees {n === 1 ? 'it' : 'them'}?</DialogTitle>
            <DialogDescription>
              You can change this on each photo, or send {n === 1 ? 'it' : 'them'} to an album later.
            </DialogDescription>
          </DialogHeader>
          <ShareWith here={family.id} value={who} onChange={setWho} onlyMeHint="Just you, until you share them" />
          <Button type="submit" size="lg" className="self-start">
            <ImagePlus aria-hidden /> Add {plural(n, 'photo')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// The albums, shelf by shelf: each collection with its albums, then the albums on no shelf.
function Shelves({
  family,
  albums,
  onStart,
  mine,
  tight,
}: {
  family: Family
  albums: Albums
  onStart: () => void
  mine?: boolean
  // Modern's Albums tab: on a phone the albums sit a thin line apart and nearly reach the screen's edges, as the photos
  // on its wall do, and the tab already names them, so a lone "Albums" heading isn't shown.
  tight?: boolean
}) {
  const loose = albums.albums.filter((x) => !x.collectionId)
  const heading = cn('text-title', tight && 'max-sm:px-2')
  return (
    <div className={cn(page, 'flex flex-col gap-12', tight && 'max-sm:gap-10 max-sm:px-0')}>
      {albums.collections.map((collection) => {
        const shelf = albums.albums.filter((x) => x.collectionId === collection.id)
        return (
          <section key={collection.id} className="flex flex-col gap-5" aria-labelledby={`c-${collection.id}`}>
            <div className={cn('flex items-center gap-2', tight && 'max-sm:px-2')}>
              <h2 id={`c-${collection.id}`} className="text-title">
                {collection.title}
              </h2>
              <span className="text-muted-foreground">{plural(shelf.length, 'album')}</span>
              {collection.mine && <CollectionMenu family={family} collection={collection} />}
            </div>
            <AlbumShelf albums={shelf} sideways tight={tight} />
          </section>
        )
      })}
      <section className="flex flex-col gap-5" aria-labelledby="albums">
        <h2 id="albums" className={cn(heading, tight && albums.collections.length === 0 && 'sr-only')}>
          {albums.collections.length > 0 ? 'Other albums' : mine ? 'Albums you started' : 'Albums'}
        </h2>
        <AlbumShelf albums={loose} tight={tight}>
          {albums.loose && albums.loose.photos > 0 && (
            <li>
              <LooseShelf family={family} loose={albums.loose} mine={mine} />
            </li>
          )}
          <li>
            <button
              type="button"
              onClick={onStart}
              className={cn('group flex w-full flex-col gap-3 rounded-[1.75rem]', link)}
            >
              <span className="grid aspect-[4/5] w-full place-items-center rounded-[1.75rem] bg-secondary transition-colors group-hover:bg-accent">
                <span className="grid size-12 place-items-center rounded-full bg-background shadow-paper transition-transform group-hover:scale-105">
                  <Plus className="size-6" aria-hidden />
                </span>
              </span>
              <span className="px-1 text-left font-medium">Start an album</span>
            </button>
          </li>
        </AlbumShelf>
      </section>
    </div>
  )
}

// The photos in no album, shelved after the albums like one more: their newest few on the front.
function LooseShelf({ family, loose, mine }: { family: Family; loose: LoosePhotos; mine?: boolean }) {
  return (
    <Link
      to={`/circle/${family.id}/photos?${new URLSearchParams(mine ? { loose: '1', mine: '1' } : { loose: '1' })}`}
      className={cn('group flex flex-col gap-3 rounded-[1.75rem]', link)}
    >
      <AlbumCover covers={loose.covers} index={2} large={(id) => albumPhotoUrl(id, 'medium')} />
      <span className="flex flex-col gap-0.5 px-1">
        <span className="truncate font-semibold">Not in an album</span>
        <span className="truncate text-sub text-muted-foreground">{plural(loose.photos, 'photo')}</span>
      </span>
    </Link>
  )
}

// A shelf of albums. A collection's shelf runs sideways on a phone, so each collection stays one row tall.
function AlbumShelf({
  albums,
  sideways,
  tight,
  children,
}: {
  albums: AlbumSummary[]
  sideways?: boolean
  tight?: boolean
  children?: ReactNode
}) {
  if (albums.length === 0 && !children) return <p className="text-muted-foreground">No albums on this shelf yet.</p>
  return (
    <ul
      className={cn(
        'gap-x-4 gap-y-8 sm:grid sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5',
        sideways ? (tight ? 'scroll-row' : '-mx-5 scroll-row px-5 sm:mx-0 sm:px-0') : 'grid grid-cols-2',
        tight && 'max-sm:gap-x-1 max-sm:gap-y-6',
      )}
    >
      {albums.map((album, i) => {
        const when = span(album.first, album.last)
        return (
          <li key={album.id} className={cn(sideways && 'w-[44%] shrink-0 snap-start sm:w-auto')}>
            <Link to={`/album/${album.id}`} className={cn('group flex flex-col gap-3 rounded-[1.75rem]', link)}>
              {sideways ? (
                <AlbumCover covers={album.covers} index={i} large={(id) => albumPhotoUrl(id, 'medium')} />
              ) : (
                // Swiped through without opening; not on a shelf that itself runs sideways.
                <AlbumPeek covers={album.covers} preview={album.preview ?? []} index={i} />
              )}
              <span className="flex flex-col gap-0.5 px-1">
                <span className="truncate font-semibold">{album.title}</span>
                {(album.mine || album.by) && (
                  <span className="truncate text-sub text-muted-foreground">
                    {album.mine ? 'By you' : `By ${album.by}`}
                  </span>
                )}
                {album.forEveryone && (
                  <span className="truncate text-sub text-muted-foreground">Shared with Everyone</span>
                )}
                <span className="truncate text-sub text-muted-foreground">
                  {plural(album.photos, 'photo')}
                  {when && ` · ${when}`}
                </span>
              </span>
            </Link>
          </li>
        )
      })}
      {children}
    </ul>
  )
}

// Renaming or taking down a collection you made. Its albums stay as they are, off the shelf.
function CollectionMenu({ family, collection }: { family: Family; collection: Collection }) {
  const queryClient = useQueryClient()
  const [renaming, setRenaming] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [title, setTitle] = useState(collection.title)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['albums', family.id] })
  const rename = useMutation({
    mutationFn: () => api(`/collections/${collection.id}`, { method: 'PATCH', body: { title: title.trim() } }),
    onSuccess: async () => {
      setRenaming(false)
      await refresh()
    },
  })
  const remove = useMutation({
    mutationFn: () => api(`/collections/${collection.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })
  return (
    <>
      <ActionMenu
        title={collection.title}
        trigger={
          <Button variant="ghost" size="icon" aria-label={`${collection.title} options`}>
            <MoreHorizontal aria-hidden />
          </Button>
        }
        groups={[
          [
            { label: 'Rename', onSelect: () => setRenaming(true) },
            { label: 'Take down', onSelect: () => setRemoving(true) },
          ],
        ]}
      />
      <FormError error={remove.error} />
      <AlertDialog open={removing} onOpenChange={setRemoving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Take down {collection.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its albums and photos stay just as they are, outside any collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()}>Take down</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename collection</DialogTitle>
            <DialogDescription>The albums in it stay as they are.</DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              rename.mutate()
            }}
          >
            <Label htmlFor="collection-rename">Collection name</Label>
            <Input
              id="collection-rename"
              required
              maxLength={80}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <FormError error={rename.error} />
            <Button type="submit" disabled={rename.isPending} className="self-start">
              Save
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
